import React, {useMemo, useState} from 'react';
import {Check, Search, X} from 'lucide-react';
import {PhoneHQAvatar, PhoneHQModal} from 'ch3-ui-lib';
import {canForwardInto} from '../../utils/forwardMessage.ts';
import {matchesSearchTerm} from '../../utilis/searchMatch.ts';

export interface ForwardTarget {
    readonly roomId: string;
    readonly name: string;
    readonly avatarUrl?: string;
    readonly isDirect: boolean;
    readonly memberCount: number;
    readonly encrypted: boolean;
}

interface ForwardMessageDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Who wrote the message and what it says, for the preview card. */
    senderName: string;
    preview: string;
    sourceEncrypted: boolean;
    targets: ForwardTarget[];
    onSend: (roomIds: string[]) => Promise<void>;
}

/**
 * Forward a message to one or more chats (D-CHAT-04/S01): preview of the
 * message, a search, chats as checkable rows, and "N selected · Cancel · Send".
 * A chat without encryption can't receive a message from an encrypted one.
 */
export const ForwardMessageDialog: React.FC<ForwardMessageDialogProps> = ({
    open, onOpenChange, senderName, preview, sourceEncrypted, targets, onSend,
}) => {
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState<string[]>([]);
    const [sending, setSending] = useState(false);

    const visible = useMemo(
        () => targets.filter(t => !query.trim() || matchesSearchTerm(t.name, query)),
        [targets, query],
    );

    const toggle = (roomId: string) =>
        setSelected(prev => prev.includes(roomId) ? prev.filter(id => id !== roomId) : [...prev, roomId]);

    const close = (next: boolean) => {
        if (!next) {
            setQuery('');
            setSelected([]);
        }
        onOpenChange(next);
    };

    const send = async () => {
        if (selected.length === 0 || sending) return;
        setSending(true);
        try {
            await onSend(selected);
            close(false);
        } finally {
            setSending(false);
        }
    };

    return (
        <PhoneHQModal open={open} onOpenChange={close} width={480} hideClose bodyClassName="!p-0"
                      description="Choose the chats to forward this message to">
            <div data-testid="chat.forward.dialog" className="flex flex-col gap-3 px-4 pb-4 pt-6">
                <div className="flex items-center px-2">
                    <h2 className="m-0 flex-1 text-lg font-semibold leading-[1.3]">Forward message</h2>
                    <button type="button" aria-label="Close" onClick={() => close(false)}
                            className="flex h-9 w-9 items-center justify-center rounded-[10px] hover:bg-surface-muted">
                        <X size={18} aria-hidden="true" className="!p-0 !text-ink"/>
                    </button>
                </div>

                <div className="mx-2 rounded-[10px] bg-surface-muted px-3 py-2.5">
                    <div className="text-[13px] font-semibold text-ink-muted-strong">{senderName}</div>
                    <div className="truncate text-sm leading-[1.45]">{preview}</div>
                </div>

                <div className="px-2">
                    <label className="flex h-9 items-center gap-2 rounded-[10px] bg-surface-muted px-3">
                        <Search size={16} aria-hidden="true" className="!p-0 !text-ink-muted"/>
                        <input type="search" value={query} onChange={e => setQuery(e.target.value)}
                               placeholder="Search chats and contacts" aria-label="Search chats and contacts"
                               className="min-w-0 flex-1 border-0 bg-transparent text-sm font-medium text-ink outline-none"/>
                    </label>
                </div>

                <div role="group" aria-label="Chats" className="flex max-h-[292px] flex-col overflow-y-auto">
                    {visible.length === 0 && (
                        <div className="px-2 py-6 text-center text-sm text-ink-muted">No chats found</div>
                    )}
                    {visible.map(target => {
                        const allowed = canForwardInto(sourceEncrypted, target.encrypted);
                        const checked = selected.includes(target.roomId);
                        const subtitle = !allowed
                            ? "Not encrypted · can't receive this message"
                            : target.isDirect ? 'Direct message' : `Group · ${target.memberCount} members`;
                        return (
                            <div key={target.roomId} role="checkbox" aria-checked={checked} aria-disabled={!allowed}
                                 tabIndex={allowed ? 0 : -1}
                                 data-testid="chat.forward.target"
                                 onClick={() => allowed && toggle(target.roomId)}
                                 onKeyDown={e => {
                                     if (allowed && (e.key === ' ' || e.key === 'Enter')) {
                                         e.preventDefault();
                                         toggle(target.roomId);
                                     }
                                 }}
                                 className={`flex h-14 shrink-0 items-center gap-3 rounded-[10px] px-2 outline-none focus-visible:ring-2 focus-visible:ring-brand-focus ${
                                     checked ? 'bg-brand-tint-subtle' : 'bg-surface'
                                 } ${allowed ? 'cursor-pointer hover:bg-surface-muted' : 'cursor-not-allowed opacity-60'}`}>
                                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md ${
                                    checked ? 'bg-brand' : 'border-2 border-fill-subtle'
                                }`}>
                                    {checked && <Check size={14} strokeWidth={3} aria-hidden="true" className="!p-0 !text-white"/>}
                                </span>
                                <PhoneHQAvatar name={target.name} src={target.avatarUrl} size="40px"/>
                                <div className="min-w-0 flex-1">
                                    <div className="truncate text-base font-semibold leading-[1.4]">{target.name}</div>
                                    <div className={`truncate text-[13px] leading-[1.35] ${checked ? 'text-ink-muted-strong' : 'text-ink-muted'}`}>{subtitle}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="flex items-center gap-2 border-t border-line px-2 pt-2">
                    <div className="flex-1 text-[13px] font-semibold text-ink-muted" aria-live="polite">
                        {selected.length > 0 ? `${selected.length} selected` : ''}
                    </div>
                    <button type="button" onClick={() => close(false)}
                            className="h-9 rounded-[10px] bg-surface-muted px-4 text-sm font-semibold text-ink hover:bg-line">
                        Cancel
                    </button>
                    <button type="button" onClick={send} disabled={selected.length === 0 || sending}
                            data-testid="chat.forward.send-btn"
                            className="h-9 rounded-[10px] bg-brand px-4 text-sm font-semibold text-white hover:bg-brand-hover disabled:bg-brand-disabled">
                        Send
                    </button>
                </div>
            </div>
        </PhoneHQModal>
    );
};

export default ForwardMessageDialog;
